import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { ExportItems, TypeFormats } from 'src/shared/types/exportItem';
import { ExportFileService } from 'src/app/gamification/command-center/services/export-file.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import moment from 'moment';
import { GamesHistoryHealthReportComponent } from '../../games-history-health-report/games-history-health-report.component';
import { ParticipantObj } from 'src/app/gamification/command-center/models/games-parameter.model';

@Component({
    selector: 'cv-health-report-export-button',
    templateUrl: './health-report-export-button.component.html',
    styleUrls: ['./health-report-export-button.component.scss']
})
export class HealthReportExportButtonComponent implements OnInit, OnChanges {
    @ViewChild(GamesHistoryHealthReportComponent) healthReportComponent!: GamesHistoryHealthReportComponent;
    
    @Input() startDate: string;
    @Input() endDate: string;
    @Input() filterText: string;
    @Input() data: any = null;
    @Input() ruleID: number;
    @Input() participants: ParticipantObj[];
    public localizationDic: Map<string, string> = new Map();

    disabled = true;
    fileName = '';
    CHUNK_SIZE = 100;
    items: ExportItems = {
        excel: {
            getData: async (format) => {
                this.disabled = true;
                const response = await this.exportHistoryReport(format);
                this.disabled = false;
                return response;
            },
        },
        csv: {
            getData: async (format) => {
                this.disabled = true;
                const response = await this.exportHistoryReport(format);
                this.disabled = false;
                return response;
            },
        }
    };

    constructor(
        private exportFile: ExportFileService,
        private locService: CVLocalizationService
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['participants']) {
            const participants = changes['participants'].currentValue; 
            this.disabled = participants == null || participants.length === 0;
          }
    }

    ngOnInit(): void {
        this.initAsync();
    }

    ngDoCheck(): void {
        if (this.healthReportComponent?.isLoading !== undefined) {
            this.disabled = this.healthReportComponent.isLoading;
        }
    }
    
    async initAsync() {
        this.localizationDic = await this.locService.GetSection("Games_History");
    }

    getLocalizationString(key, defaultValue) {
        if (this.localizationDic && this.localizationDic.has(key))
            return this.localizationDic.get(key);
        return defaultValue;
    }
    
    async sendChunkedData(data: any, url: string, requestId: string, format): Promise<any> {
        const { ghObjects, game, columnStates, totalRow } = data;

        const totalChunks = Math.ceil(ghObjects.length / this.CHUNK_SIZE);

        for (let i = 0; i < totalChunks; i++) {
            const chunk = ghObjects.slice(i * this.CHUNK_SIZE, (i + 1) * this.CHUNK_SIZE);
            const isLastChunk = i === totalChunks - 1;

            const body = {
                ghObjects: chunk,
                game,
                columnStates,
                totalRow,
                chunkIndex: i,
                isLastChunk,
                requestId,
                format
            };

            const response = await this.sendChunk(url, body);
            if (!response || response.status_code !== 200) {
                throw new Error(`Failed to send chunk ${i}`);
            }
            // If it's the last chunk, wait for the server's final response
            if (isLastChunk) {
                return response;
            }
        }
    }

    async sendChunk(url: string, body: any) {
        try {
            const response = await this.exportFile.exportHistoryReport(url, body);
            return response;
        } catch (error) {
            return null;
        }
    }


    generateUUID() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0,
                v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    waitForData(): Promise<void> {
        return new Promise((resolve) => {
            const checkData = () => {
                if (
                    this.data &&
                    this.data.ghObjects &&
                    this.data.ghObjects.length > 0 &&
                    this.data.ghObjects[0] !== undefined &&
                    this.data.topPerformersData !== undefined &&
                    Array.isArray(this.data.topPerformersData) &&
                    this.data.gameEffectivenessData !== undefined &&
                    Array.isArray(this.data.gameEffectivenessData) &&
                    this.data.game &&
                    this.data.game.GameDetails !== undefined
                ) {
                    resolve();
                } else {
                    setTimeout(checkData, 500);
                }
            };
            checkData();
        });
    }


    async exportHistoryReport(format) {
        await this.waitForData(); // Wait until the data is correctly assigned

        if (!this.endDate || !this.startDate || !this.ruleID || !this.data) {
            return '';
        }

        this.fileName = this.getLocalizationString('Games_History', 'Games history') + ' ';
        let startDate = moment(this.startDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss');
        let endDate = moment(this.endDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss');

        const body = this.data;
        const url = `/api/v1/gamification/game/game-health/${this.ruleID}/participants/export?format=${format}&start=${startDate}&end=${endDate}`;

        if (body.ghObjects.length <= 200) {
            return this.exportFile.exportReport("/api/v1/gamification/game/game-health/" + this.ruleID + "/participants/export?format=" + format + "&start=" + startDate + "&end=" + endDate, body);
        } else {
            // Send data in chunks
            const requestId = this.generateUUID();
            const result = await this.sendChunkedData(body, url, requestId, format);
            this.data = {};
            return result.data;
        }
    }

}
